/**
 * Normalizes a card number string to group digits in blocks of four separated by spaces.
 *
 * @param {string} value - Raw card number input
 * @returns {string} Formatted card number (e.g. "0000 0000 0000 0000")
 */
export const formatCardNumber = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

export const CARD_TYPES = Object.freeze({
  MADA: "MADA",
  VISA: "VISA",
  MASTERCARD: "MASTERCARD",
  AMEX: "AMEX",
  UNKNOWN: "UNKNOWN",
});

/**
 * Detects card network brand from card number (digits only or formatted).
 * @param {string} cardNumber - Raw or formatted card number input
 * @returns {typeof CARD_TYPES[keyof typeof CARD_TYPES] | null}
 */
export const detectCardType = (cardNumber) => {
  if (!cardNumber) return null;
  const digits = String(cardNumber).replace(/\D/g, "");
  if (!digits) return null;

  // Mada BINs check (Saudi National Payment Scheme)
  const mada6Prefixes = new Set([
    "588845", "588846", "588847", "588848", "588849", "588850", "588851", "588852",
    "588853", "588854", "588855", "588856", "588857", "588858", "588859", "588860",
    "588861", "588862", "588863", "588864", "588865", "588866", "588867", "588868",
    "588869", "588870", "588871", "588872", "588873", "588874", "588875", "588876",
    "588877", "588878", "588879", "588880", "588881", "588882", "588883", "588884",
    "588885", "588886", "588887", "588888", "588889", "588890", "588891", "588892",
    "588893", "588894", "588895", "588896", "588897", "588898", "588899", "589005",
    "589206", "531095", "530060", "532013", "535989", "535825", "536023", "537767",
    "539773", "557606", "558848", "521076", "529415", "529741", "400861", "401757",
    "409201", "410685", "417633", "419593", "422817", "422818", "422819", "428671",
    "428672", "428673", "431361", "432328", "434107", "439954", "440533", "440647",
    "440795", "445564", "446393", "446404", "446672", "455036", "455708", "457865",
    "457997", "462220", "468540", "468541", "468542", "468543", "483010", "483011",
    "483012", "484783", "486094", "486095", "486096", "489318", "489319", "490405",
    "490425", "490426", "490428", "490429", "491686", "493428", "604906", "605141",
    "636120", "968201", "968202", "968203", "968204", "968205", "968206", "968207",
    "968208", "968209", "968210", "968211"
  ]);

  if (digits.length >= 4) {
    const p6 = digits.slice(0, 6);
    if (mada6Prefixes.has(p6)) {
      return CARD_TYPES.MADA;
    }
    if (
      digits.startsWith("5888") ||
      digits.startsWith("5890") ||
      digits.startsWith("5892") ||
      digits.startsWith("9682")
    ) {
      return CARD_TYPES.MADA;
    }
  } else if (digits.length >= 1) {
    if (
      digits.startsWith("5888") ||
      digits.startsWith("5890") ||
      digits.startsWith("5892") ||
      digits.startsWith("9682")
    ) {
      return CARD_TYPES.MADA;
    }
  }

  // Visa: Starts with 4
  if (digits.startsWith("4")) {
    return CARD_TYPES.VISA;
  }

  // Mastercard: Starts with 51-55 or 2221-2720
  const first2 = parseInt(digits.slice(0, 2), 10);
  const first4 = parseInt(digits.slice(0, 4), 10);
  if ((first2 >= 51 && first2 <= 55) || (first4 >= 2221 && first4 <= 2720)) {
    return CARD_TYPES.MASTERCARD;
  }

  // Amex: Starts with 34 or 37
  if (digits.startsWith("34") || digits.startsWith("37")) {
    return CARD_TYPES.AMEX;
  }

  return CARD_TYPES.UNKNOWN;
};
